/*
 * Les bornes de Nextilt, écrites une fois (chantier nettoyage-contrat, ticket 01) : ce que le
 * serveur accepte, et ce que l'app et le MCP sauront dire avant d'envoyer. Des nombres, rien
 * d'autre : les règles zod qui les appliquent sont dans `champs.ts`, une entrée à part
 * (`@nextilt/commun/champs`, ticket 02).
 */
/** Les bornes des champs d'une bulle (`modificationNoeud`, le plan importé, les modèles). */
export const BORNES_BULLE = {
    /** Un titre, en caractères. */
    titreMax: 300,
    /** Une note, en caractères. */
    notesMax: 20_000,
    /** Le rang donné à des sœurs : de 1 à 999. */
    rangMin: 1,
    rangMax: 999,
    /** La durée d'un rendez-vous, en minutes : de 1 minute à un jour (décision d'Igor du 2026-10-07). */
    dureeMinMin: 1,
    dureeMinMax: 24 * 60,
    /**
     * Ce qu'une tâche te prend (`effortMin`), en minutes : de 1 minute à 24 heures (chantier
     * duree-a-la-minute, décision d'Igor du 2026-10-11).
     */
    effortMinMin: 1,
    effortMinMax: 24 * 60,
    /** Le délai du monde, en jours (dix ans). */
    delaiJoursMax: 3650,
    /** Le même délai, en mois (dix ans). */
    delaiMoisMax: 120,
    /** La difficulté, en étoiles. */
    chargeMin: 1,
    chargeMax: 5,
    /** Le lieu, en caractères. */
    lieuTexteMax: 300,
    /** Le contact. Une adresse e-mail fait au plus 320 caractères. */
    contactNomMax: 200,
    contactTelMax: 50,
    contactEmailMax: 320,
    /** Un lien, en caractères. */
    lienMax: 2000,
};
/**
 * Les années d'une date, bornes comprises : avant 1900 ou après 2199, c'est une année qu'on est
 * encore en train de taper (0002, 0020, 0202 avant 2026), pas une date qu'on veut poser.
 */
export const ANNEES_POSSIBLES = { premiere: 1900, derniere: 2199 };
/**
 * Une profondeur d'arbre au-delà de laquelle on refuse d'écrire.
 * Un arbre de 10 000 niveaux fait exploser la pile dans les calculs récursifs
 * (docs/securite.md, « Les règles », 9.3), et personne n'imbrique un projet réel plus de
 * vingt fois.
 */
export const PROFONDEUR_MAX = 40;
/** Le nombre de bulles qu'un seul dossier a le droit de contenir. */
export const TAILLE_SOUS_ARBRE_MAX = 5000;
/**
 * Le nombre total de bulles d'un utilisateur.
 *
 * Chaque vue calculée charge le graphe ENTIER d'un utilisateur en mémoire (voir
 * `chargerGraphe`) : un choix assumé pour quelques comptes d'amis, mais il n'a de sens que
 * borné. Sans ce plafond, une file de capture qui rejoue en boucle suffit à rendre l'écran
 * d'accueil inutilisable, et un compte trop gros ralentirait l'app de tous les autres (un seul
 * processus, un seul cœur dans le conteneur en ligne).
 *
 * Volontairement haut : un usage réel n'en approchera jamais.
 */
export const BULLES_MAX_PAR_UTILISATEUR = 20_000;
/**
 * Le nombre de rangs posés d'un seul geste (« Hiérarchiser », `POST /api/noeuds/rangs`, l'outil
 * `ordonner` du MCP). Une liste vide est permise : elle ne change rien.
 */
export const RANGS_PAR_GESTE_MAX = 500;
/**
 * Les bornes de la recherche par titre (`GET /api/recherche`, « Lier » dans l'app, l'outil
 * `chercher` du MCP) : en dessous de 2 signes on ne cherche pas, un texte de plus de 200 signes est
 * refusé, et 40 résultats au plus reviennent.
 */
export const BORNES_RECHERCHE = { signesMin: 2, signesMax: 200, resultatsMax: 40 };
/**
 * Les bornes d'un papier posé sur la carte, post-it ou liste libre (chantier post-it-et-listes,
 * ticket 01). Les longueurs et les nombres sont refusés au-delà ; la taille et la place, elles,
 * sont ramenées aux bornes (`bornerTaille`, `bornerPlace`, `server/src/domain/papiers/`) : une
 * poignée tirée trop loin ne doit pas faire échouer le geste. En pixels de la carte, la place
 * relative au centre de la carte, à celui du papier.
 */
export const BORNES_PAPIER = {
    /** Un titre, en caractères (facultatif). */
    titreMax: 120,
    /** Le texte d'un post-it, en caractères. */
    texteMax: 5000,
    /** Une ligne d'une liste, en caractères. */
    ligneMax: 300,
    /** Le nombre de lignes d'une liste. */
    lignesMax: 100,
    /** La taille d'un papier : la maquette dessine un post-it de 160 px et une liste de 260 px. */
    largeurMin: 120,
    largeurMax: 640,
    hauteurMin: 90,
    hauteurMax: 900,
    /** La place, de part et d'autre du centre, sur chaque axe. */
    placeMax: 20_000,
    /** Le nombre de papiers posés sur une même bulle. */
    papiersParBulle: 50,
};
/**
 * Les bornes de la place gardée d'une bulle sur la carte (chantier deplacer-les-bulles, ticket
 * 01) : son décalage, en pixels entiers, par rapport au centre de sa mère, de part et d'autre sur
 * chaque axe. Large, comme la place d'un papier : la carte la plus grande tient dedans. Au-delà,
 * la demande est refusée (une bulle ne se lâche jamais si loin).
 */
export const BORNES_PLACE_GARDEE = {
    decalageMax: 20_000,
};
/**
 * Les bornes de la boîte d'entrée (chantier boite-d-entree, ticket 01). Le texte d'une note est
 * borné comme un titre de bulle (`BORNES_BULLE.titreMax`) : rangée, elle en devient un. Le nombre
 * de notes d'un compte est plafonné, comme les papiers d'une bulle : une boîte se vide, et un
 * appel qui boucle ne doit pas la remplir sans fin.
 */
export const BORNES_BOITE = {
    notesMax: 1000,
};
/**
 * Les bornes du profil (chantier profil, ticket 01) : chaque champ, chaque intitulé et chaque
 * valeur d'une ligne libre est un texte court ; au-delà, refusé.
 */
export const BORNES_PROFIL = {
    /** Un champ, un intitulé ou une valeur, en caractères (décision d'Igor du 2026-10-21). */
    texteMax: 200,
    /** Le nombre de lignes libres d'une feuille. */
    lignesParFeuille: 50,
};
/** Au-delà, ce n'est plus une journée : la route refuse. */
export const TACHES_MAX_PAR_JOURNEE = 40;
/** Les bornes d'un plan écrit par l'IA de l'utilisateur (`server/src/domain/import-ia/plan.ts`). */
export const LIMITES_PLAN = {
    /** En caractères. Les essais du 2026-09-24, notes comprises, en font moins de 400 000. */
    texte: 1_000_000,
    bulles: 2000,
    profondeur: 20,
    liens: 2000,
    /** Au-delà, la liste des erreurs se résume : mille lignes n'aident personne. */
    erreurs: 20,
};
/**
 * Les bornes d'un modèle (`server/src/domain/modeles/template.ts`, templates/schema-template.md). La clé
 * et la note de relecture d'un lien valent aussi pour le plan, variante de ce format.
 */
export const BORNES_MODELE = {
    profondeur: 8,
    bulles: 500,
    liens: 200,
    /** Une clé : minuscules, chiffres, tiret et souligné, au plus 60 caractères. */
    cleMax: 60,
    /** La note de relecture d'un lien (`pourquoi`), jamais chargée en base. */
    pourquoiMax: 300,
    sourcesMax: 300,
    /** La présentation : au plus 10 lignes de 300 caractères par liste, et un avertissement. */
    lignesDePresentation: 10,
    ligneDePresentationMax: 300,
    avertissementMax: 2000,
    /** Un rendez-vous ou une échéance de démonstration : au plus dans deux ans. */
    dansJoursMax: 730,
};
